// src/app/features/conversations/FloatingChatProvider.tsx
import React, { createContext, useContext, useState } from 'react';

/** The shape of the data stored by our FloatingChatProvider. */
interface FloatingChatContextProps {
  /** Whether the floating chat is currently shown or hidden. */
  isOpen: boolean;
  /** Open (show) the floating chat. */
  openChat: () => void;
  /** Close (hide) the floating chat. */
  closeChat: () => void;
}

/** We create a React Context to hold our floating chat's open/close state. */
const FloatingChatContext = createContext<FloatingChatContextProps>({
  isOpen: false,
  openChat: () => {},
  closeChat: () => {},
});

/**
 * Provides open/close state to any children who call `useFloatingChat()`.
 */
export const FloatingChatProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const openChat = () => setIsOpen(true);
  const closeChat = () => setIsOpen(false);

  return (
    <FloatingChatContext.Provider value={{ isOpen, openChat, closeChat }}>
      {children}
    </FloatingChatContext.Provider>
  );
};

/**
 * Call this inside a child component to get `isOpen`, `openChat()`, `closeChat()`.
 */
export const useFloatingChat = () => useContext(FloatingChatContext);
